import classNames from "classnames";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { AvisConstants } from "../../../../data/avisConstants";
import { PortalAdditionalReportParamViewModel } from "../../../../generated/data-contracts";
import { TimeUtils } from "../../../../utils";
import {
  AvisCalendar,
  AvisCalendarYear,
} from "../../../shared/components/inputs";
import { AvisContext } from "../../../shared/context/AvisContext";

interface ISelectedReportParamBoxItemProps {
  parameter: PortalAdditionalReportParamViewModel;
  onChange: (paramx: string, value: string) => void;
  reportParamValue: string | undefined;
  isSubmitting: boolean;
  fieldValidationTexts: Record<string, string>;
}

export function SelectedReportParamBoxItem(
  props: Readonly<ISelectedReportParamBoxItemProps>,
) {
  const { res } = useContext(AvisContext);
  const {
    parameter,
    onChange,
    reportParamValue,
    isSubmitting,
    fieldValidationTexts,
  } = props;
  const [selectedValue, setSelectedValue] = useState<Date | Date[] | undefined>(
    undefined,
  );
  const [validationText, setValidationText] = useState<string | undefined>();

  const maxDate = moment();
  const minDate = moment().add(-10, "years").startOf("year");
  const yearTo = maxDate.year();
  const yearFrom = minDate.year();
  const yearRangeStr = `${yearFrom}:${yearTo}`;
  const rowClassName = classNames("row-value");

  const onChangeHandler = (value: Date | Date[]) => {
    let valueDate: Date = value as Date;

    //If you write manually in year picker - you get string value
    if (
      typeof valueDate === "string" &&
      parameter.paramType === "Year" &&
      !isNaN(parseInt(valueDate))
    ) {
      valueDate = moment({
        year: parseInt(valueDate),
        month: 0,
        day: 1,
      }).toDate();
    }

    const selectedDate = TimeUtils.getMomentExactFormat(valueDate);
    setSelectedValue(valueDate);

    if (parameter.reportParameter && selectedDate.isValid()) {
      setValidationText(undefined);

      if (selectedDate.isBefore(minDate) || selectedDate.isAfter(maxDate)) {
        setValidationText(
          res(
            "authors_ReportsPage_ReportDetails_validation_dateRange",
            yearFrom.toFixed(),
            yearTo.toFixed(),
          ),
        );
        onChange(parameter.reportParameter, "");
        return;
      }

      if (parameter.paramType === "Year")
        onChange(parameter.reportParameter, selectedDate.year().toString());
      else if (parameter.paramType === "Date")
        onChange(
          parameter.reportParameter,
          selectedDate.format(AvisConstants.date_format),
        );
    } else {
      setValidationText(
        res("authors_ReportsPage_ReportDetails_validation_chooseDate"),
      );
      onChange(parameter.reportParameter, "");
    }
  };
  useEffect(() => {
    if (!reportParamValue) return;

    if (parameter.paramType === "Year") {
      const defaultValue = moment({
        year: parseInt(reportParamValue),
        month: 0,
        day: 1,
      });
      onChangeHandler(defaultValue.toDate());
    } else if (parameter.paramType === "Date") {
      const defaultValue = TimeUtils.getMomentExactFormat(reportParamValue);
      if (defaultValue.isValid()) onChangeHandler(defaultValue.toDate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportParamValue, parameter.paramType]);

  const getAgregatedValidationText = () => {
    if (validationText) return validationText;
    if (fieldValidationTexts[parameter.reportParameter])
      return fieldValidationTexts[parameter.reportParameter];
  };
  const renderProp = () => {
    if (parameter.paramType === "Year") {
      return (
        <div className={rowClassName}>
          <AvisCalendarYear
            label={parameter.paramName}
            value={selectedValue}
            yearRange={yearRangeStr}
            error={getAgregatedValidationText()}
            disabled={isSubmitting}
            hideClear
            onChange={(e) => {
              onChangeHandler(e.value);
            }}
          />
        </div>
      );
    }
    if (parameter.paramType === "Date") {
      return (
        <div className={rowClassName}>
          <AvisCalendar
            label={parameter.paramName}
            value={selectedValue}
            minDate={minDate.toDate()}
            maxDate={maxDate.toDate()}
            yearRange={yearRangeStr}
            error={getAgregatedValidationText()}
            disabled={isSubmitting}
            hideClear
            onChange={(e) => {
              onChangeHandler(e.value);
            }}
          />
        </div>
      );
    }
  };

  return <div className="selected-report-param">{renderProp()}</div>;
}
